import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Copy, Download, ImageDown, MessageCircle } from "lucide-react";
import { money, fmtDate } from "../lib/api";
import { shareQrToWhatsapp, canCopyQrImageToClipboard, copyQrImageToClipboard } from "../lib/pixShare";

// Correção PIX Asaas — "Ver PIX" para cobrança PIX via provedor INTEGRADO
// (hoje Mercado Pago/Asaas — nunca PIX por chave, que já tem seu próprio
// modal em PixChaveModal.jsx e nunca deve ser confundido com este).
// Mesmo padrão visual/campos já homologados em Financeiro.jsx (mensalidade
// SaaS) — aqui generalizado para ser reaproveitado também pelo
// Faturamento operacional do tenant, uma ÚNICA implementação de UI para
// os dois lugares que hoje exibem PIX integrado. Usa exatamente os dados
// REAIS já persistidos pelo backend (pix_qr_code/pix_qr_code_base64,
// vindos do próprio provedor) — nunca gera um QR local diferente.
const BOLETO_STATUS_LABEL = {
  waiting_payment: "Aguardando pagamento", processing: "Processando pagamento", paid: "Pago",
  cancelled: "Cancelado", expired: "Vencido", failed: "Rejeitado", unknown: "Status desconhecido",
};

export default function IntegratedPixModal({ open, onOpenChange, title, boleto, onCopy, onGetWhatsappLink, filename = "pix-qrcode.png" }) {
  const providerLabel = boleto?.payment_provider === "asaas" ? "Asaas" : boleto?.payment_provider === "mercado_pago" ? "Mercado Pago" : "-";
  const isSandbox = boleto?.environment === "test" || boleto?.environment === "sandbox";
  const statusLabel = BOLETO_STATUS_LABEL[boleto?.boleto_status || boleto?.status] || boleto?.boleto_status || boleto?.status || "-";
  const [sharingWhatsapp, setSharingWhatsapp] = useState(false);
  const [copyingQrImage, setCopyingQrImage] = useState(false);

  // Item 3 do pedido — mesmo mecanismo de PixChaveModal, aqui a partir do
  // QR já fornecido pelo provedor (pix_qr_code_base64, nunca gerado
  // localmente de novo): cria um File real (qr-pix-<cobrança>.png) e
  // tenta a Web Share API (canShare -> share) com texto + arquivo — abre
  // o share sheet nativo, nunca um link wa.me nesse caminho. Ajuste
  // complementar, item 2 — sem suporte, NUNCA dispara download
  // automático; avisa e aponta para "Copiar QR Code"/"Baixar QR Code".
  const shareWhatsappComQr = async () => {
    if (!onGetWhatsappLink) return;
    setSharingWhatsapp(true);
    try {
      const waLink = await onGetWhatsappLink();
      const result = await shareQrToWhatsapp({ base64Png: boleto?.pix_qr_code_base64, waLink, filename });
      if (result.shared) toast.success("QR Code e mensagem compartilhados.");
      else if (result.cancelled) { /* cancelado pelo usuário na folha de compartilhamento */ }
      else if (result.fallback && result.reason === "sem-suporte-anexo") toast.info("Seu navegador não permite anexar automaticamente o QR Code ao WhatsApp. WhatsApp aberto com a mensagem — copie ou baixe o QR Code ao lado para anexar manualmente.", { duration: 8000 });
      else toast.info("WhatsApp aberto com a mensagem pronta — confirme o envio por lá.");
    } catch {
      toast.error("Não foi possível preparar o compartilhamento por WhatsApp.");
    } finally {
      setSharingWhatsapp(false);
    }
  };

  // Ajuste complementar, item 2 — baixar manualmente o MESMO PNG já
  // fornecido pelo provedor (nunca gera um QR novo), mesmo padrão do botão
  // "Baixar QR Code" que o PixChaveModal já tinha.
  const downloadQr = () => {
    if (!boleto?.pix_qr_code_base64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${boleto.pix_qr_code_base64}`;
    a.download = filename;
    a.click();
  };

  // Correção — Modal Faturamento/Compartilhamento, item 2 (fallback) —
  // Copiar QR Code como imagem de verdade (Clipboard API), a partir do
  // MESMO PNG do provedor.
  const copiarQrComoImagem = async () => {
    setCopyingQrImage(true);
    try {
      const result = await copyQrImageToClipboard({ base64Png: boleto?.pix_qr_code_base64 });
      if (result.copied) toast.success("QR Code copiado como imagem.");
      else toast.error("Não foi possível copiar o QR Code como imagem.");
    } finally {
      setCopyingQrImage(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="integrated-pix-modal">
        <DialogHeader><DialogTitle>{title || "PIX Copia e Cola"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-center">
          {boleto?.pix_qr_code_base64 ? (
            <img src={`data:image/png;base64,${boleto.pix_qr_code_base64}`} alt="QR Code PIX" className="mx-auto w-48 h-48 border border-slate-200 rounded-md" data-testid="integrated-pix-qrcode" />
          ) : (
            <p className="text-sm text-slate-500">QR Code indisponível — use o Copia e Cola abaixo.</p>
          )}
          <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
            <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{money(boleto?.valor ?? boleto?.valor_final)}</span></div>
            <div><span className="text-slate-500">Vencimento:</span> <span className="font-medium text-slate-900">{fmtDate(boleto?.vencimento)}</span></div>
            <div><span className="text-slate-500">Status:</span> <span className="font-medium text-slate-900">{statusLabel}</span></div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Provedor:</span>
              <Badge className="bg-blue-100 text-blue-800">{providerLabel}</Badge>
              {isSandbox && <Badge className="bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
            </div>
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
            Antes de pagar, confira se os dados do recebedor exibidos pelo seu banco correspondem à empresa responsável pela cobrança.
          </div>
          {boleto?.pix_qr_code && (
            <div>
              <div className="text-xs text-slate-500 mb-1 text-left">PIX Copia e Cola</div>
              <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-md p-3 break-all text-left" data-testid="integrated-pix-copia-cola">{boleto.pix_qr_code}</div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {boleto?.pix_qr_code && (
            <Button variant="outline" onClick={() => onCopy(boleto.pix_qr_code, "PIX Copia e Cola")} data-testid="integrated-pix-copiar">
              <Copy size={14} /> Copiar código PIX
            </Button>
          )}
          {boleto?.pix_qr_code_base64 && (
            <Button variant="outline" onClick={downloadQr} data-testid="integrated-pix-baixar-qr">
              <Download size={14} /> Baixar QR Code
            </Button>
          )}
          {boleto?.pix_qr_code_base64 && canCopyQrImageToClipboard() && (
            <Button variant="outline" onClick={copiarQrComoImagem} disabled={copyingQrImage} data-testid="integrated-pix-copiar-qr-imagem">
              <ImageDown size={14} /> {copyingQrImage ? "Copiando..." : "Copiar QR Code"}
            </Button>
          )}
          {onGetWhatsappLink && (
            <Button variant="outline" onClick={shareWhatsappComQr} disabled={sharingWhatsapp} data-testid="integrated-pix-whatsapp">
              <MessageCircle size={14} /> {sharingWhatsapp ? "Preparando..." : "WhatsApp"}
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="integrated-pix-fechar">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
